import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getStockItemById } from '@/lib/queries/stockItems'
import { getStockBundlesByStockItemId } from '@/lib/queries/bundles'
import type { SupplierStockItem } from '@/types/supplierStockItem'
import type { CategoryType } from '@/types/categoryType'

/**
 * Stock Item Detail page
 * Mobile-first design optimized for iPad Mini
 * Based on wireframe: stock-item-detail.html
 */
export default function StockItemDetail() {
  const { id } = useParams<{ id: string }>()

  const { data: stockItem, isLoading, error } = useQuery({
    queryKey: ['stockItem', id],
    queryFn: () => getStockItemById(id!, true), // Use fresh data (bypass CDN cache)
    enabled: !!id,
    // Ensure we always get fresh data (no stale cache)
    staleTime: 0,
    gcTime: 0, // Previously cacheTime in React Query v4
  })

  // Fetch bundles that use this stock item
  // Use the route id directly since it's the stock item ID
  const { data: bundles = [], isLoading: bundlesLoading, error: bundlesError } = useQuery({
    queryKey: ['stockBundlesByStockItem', id],
    queryFn: () => {
      console.log('Fetching bundles for stock item:', id)
      return getStockBundlesByStockItemId(id!)
    },
    enabled: !!id,
    staleTime: 0, // No caching - always fetch fresh data
    gcTime: 0, // Don't keep in cache
    retry: 2, // Retry failed requests
  })

  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('StockItemDetail bundles state:', {
      id,
      bundlesLoading,
      bundlesError,
      bundlesCount: bundles.length,
      bundles
    })
  }

  // Helper to get supplierItem (populated or null)
  const getSupplierStockItem = (): SupplierStockItem | null => {
    if (!stockItem || !stockItem.supplierItem) return null
    
    const ssi = stockItem.supplierItem
    
    // If it's not an object, return null
    if (typeof ssi !== 'object' || ssi === null) return null
    
    // If it's a reference object ({_ref, _type}), return null (not populated)
    if ('_ref' in ssi && '_type' in ssi && !('name' in ssi)) {
      return null
    }
    
    // If it has name property, it's populated
    if ('name' in ssi || 'sku' in ssi) {
      return ssi as SupplierStockItem
    }
    
    return null
  }

  // Helper to get category (populated or null)
  const getCategory = (): CategoryType | null => {
    if (!stockItem || !stockItem.category) {
      console.log('Detail - No category in stockItem')
      return null
    }
    
    const cat = stockItem.category
    
    console.log('Detail - Category data:', {
      category: cat,
      categoryType: typeof cat,
      isObject: typeof cat === 'object' && cat !== null,
      hasRef: '_ref' in (cat as any),
      hasType: '_type' in (cat as any),
      hasName: 'name' in (cat as any),
      hasId: '_id' in (cat as any),
      categoryKeys: cat && typeof cat === 'object' ? Object.keys(cat) : null,
      categoryName: (cat as any)?.name,
      categoryRef: (cat as any)?._ref,
    })
    
    // If it's not an object, return null
    if (typeof cat !== 'object' || cat === null) return null
    
    // If it's a reference object ({_ref, _type}), return null (not populated)
    if ('_ref' in cat && '_type' in cat && !('name' in cat)) {
      console.log('Detail - Category is a reference object, not populated')
      return null
    }
    
    // If it has name property, it's populated
    if ('name' in cat && typeof (cat as any).name === 'string') {
      console.log('Detail - Category is populated:', (cat as any).name)
      return cat as CategoryType
    }
    
    console.log('Detail - Category format not recognized')
    return null
  }

  const formatAttributes = (attributes?: Record<string, any>) => {
    if (!attributes || Object.keys(attributes).length === 0) {
      return <div className="text-gray-500 text-sm">No attributes</div>
    }

    const formatValue = (value: any): string => {
      if (value === null || value === undefined) return 'N/A'
      if (Array.isArray(value)) return value.join(', ')
      if (typeof value === 'object') {
        // Don't try to render objects - could be references
        return JSON.stringify(value)
      }
      return String(value)
    }

    return (
      <div className="space-y-1 text-sm text-gray-600">
        {Object.entries(attributes).map(([key, value]) => (
          <div key={key}>
            <span className="capitalize font-medium">{key}:</span>{' '}
            {formatValue(value)}
          </div>
        ))}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (error || !stockItem) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Stock item not found</p>
          <Link
            to="/stock-items"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium"
          >
            Back to List
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-900">Stock Item Detail</h1>
          <div className="flex gap-3">
            <Link
              to={`/stock-items/${id}/edit`}
              className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center touch-target-min-44 hover:bg-gray-200 transition-colors"
              aria-label="Edit"
            >
              ✏️
            </Link>
            <button
              className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center touch-target-min-44 hover:bg-gray-200 transition-colors"
              aria-label="More options"
            >
              ⋯
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {/* Item Image - Compact version for stock items without images */}
        {stockItem && stockItem.images && 
         Array.isArray(stockItem.images) && 
         stockItem.images.length > 0 && 
         typeof stockItem.images[0] === 'string' ? (
          <div className="w-full aspect-square bg-gray-100 rounded-lg mb-4 flex items-center justify-center">
            <img
              src={stockItem.images[0]}
              alt={stockItem.name}
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
        ) : null}

        {/* Basic Information Card */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {stockItem?.name && typeof stockItem.name === 'string' ? stockItem.name : 'Unnamed Item'}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            SKU: {stockItem?.sku && typeof stockItem.sku === 'string' ? stockItem.sku : 'N/A'}
          </p>

          <div className="space-y-4">
            {/* Category */}
            <div>
              <div className="text-xs text-gray-600 mb-1">Category</div>
              <div>
                {(() => {
                  const category = getCategory()
                  return category ? (
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                      {category.name}
                    </span>
                  ) : null
                })()}
              </div>
            </div>

            {/* Description */}
            {stockItem?.description && typeof stockItem.description === 'string' && (
              <div>
                <div className="text-xs text-gray-600 mb-1">Description</div>
                <div className="text-base text-gray-900">{stockItem.description}</div>
              </div>
            )}

            {/* Unit of Measure */}
            <div>
              <div className="text-xs text-gray-600 mb-1">Unit of Measure</div>
              <div className="text-base text-gray-900">Each</div>
            </div>
          </div>
        </div>

        {/* Supplier Information Card */}
        {stockItem.supplier && (
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Supplier</h3>
            <div className="space-y-4">
              {stockItem.supplier.name && (
                <div>
                  <div className="text-xs text-gray-600 mb-1">Supplier Name</div>
                  <div className="text-base text-gray-900">{stockItem.supplier.name}</div>
                </div>
              )}
              
              {stockItem.supplier.stockSupplierSource?.name && (
                <div>
                  <div className="text-xs text-gray-600 mb-1">Supplier Source</div>
                  <div className="text-base text-gray-900">
                    {stockItem.supplier.stockSupplierSource.name}
                    {stockItem.supplier.stockSupplierSource.rate && (
                      <span className="text-sm text-gray-500 ml-2">
                        (Rate: {stockItem.supplier.stockSupplierSource.rate})
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {stockItem.supplier.url && (
                <div>
                  <div className="text-xs text-gray-600 mb-1">URL</div>
                  <a
                    href={stockItem.supplier.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base text-blue-600 hover:underline"
                  >
                    {stockItem.supplier.url}
                  </a>
                </div>
              )}
              
              {stockItem.supplier.flags?.isFavourite && (
                <div>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                    ⭐ Favourite
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Supplier Stock Item Card */}
        {stockItem?.supplierItem && (
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Supplier Stock Item</h3>
            {(() => {
              const supplierItem = getSupplierStockItem()
              
              if (!supplierItem) {
                // Reference exists but not populated
                return (
                  <div className="text-sm text-gray-500">
                    Supplier stock item reference exists but details are not available.
                  </div>
                )
              }
              
              // Helper to get category name
              const getCategoryName = (category: any): string | undefined => {
                if (!category || typeof category !== 'object') return undefined
                if ('_ref' in category && !('name' in category)) return undefined
                if ('name' in category) return category.name
                return undefined
              }
              
              // Helper to get unit name
              const getUnitName = (unit: any): string | undefined => {
                if (!unit || typeof unit !== 'object') return undefined
                if ('_ref' in unit && !('name' in unit)) return undefined
                if ('name' in unit) return unit.name
                return undefined
              }
              
              // Helper to get colour name
              const getColourName = (colour: any): string | undefined => {
                if (!colour || typeof colour !== 'object') return undefined
                if ('_ref' in colour && !('name' in colour)) return undefined
                if ('name' in colour) return colour.name
                return undefined
              }
              
              // Helper to get shape name
              const getShapeName = (shape: any): string | undefined => {
                if (!shape || typeof shape !== 'object') return undefined
                if ('_ref' in shape && !('name' in shape)) return undefined
                if ('name' in shape) return shape.name
                return undefined
              }
              
              return (
                <div className="space-y-4">
                  {/* Basic Information */}
                  {supplierItem.name && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Item Name</div>
                      <div className="text-base text-gray-900">{supplierItem.name}</div>
                    </div>
                  )}
                  
                  {supplierItem.sku && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">SKU</div>
                      <div className="text-base text-gray-900">{supplierItem.sku}</div>
                    </div>
                  )}
                  
                  {supplierItem.description && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Description</div>
                      <div className="text-base text-gray-900">{supplierItem.description}</div>
                    </div>
                  )}
                  
                  {/* Category */}
                  {getCategoryName(supplierItem.category) && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Category</div>
                      <div className="text-base text-gray-900">{getCategoryName(supplierItem.category)}</div>
                    </div>
                  )}
                  
                  {/* Unit */}
                  {getUnitName(supplierItem.unit) && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Unit</div>
                      <div className="text-base text-gray-900">{getUnitName(supplierItem.unit)}</div>
                    </div>
                  )}
                  
                  {/* Quantity per Unit */}
                  {supplierItem.quantityPerUnit !== undefined && supplierItem.quantityPerUnit !== null && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Quantity per Unit</div>
                      <div className="text-base text-gray-900">{supplierItem.quantityPerUnit}</div>
                    </div>
                  )}
                  
                  {/* Brand */}
                  {supplierItem.brand && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Brand</div>
                      <div className="text-base text-gray-900">{supplierItem.brand}</div>
                    </div>
                  )}
                  
                  {/* Manufacturer */}
                  {supplierItem.manufacturer && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Manufacturer</div>
                      <div className="text-base text-gray-900">{supplierItem.manufacturer}</div>
                    </div>
                  )}
                  
                  {/* Dimensions */}
                  {(supplierItem.length || supplierItem.width) && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Dimensions</div>
                      <div className="text-base text-gray-900">
                        {supplierItem.length && supplierItem.width
                          ? `${supplierItem.length} × ${supplierItem.width} mm`
                          : supplierItem.length
                          ? `${supplierItem.length} mm`
                          : supplierItem.width
                          ? `${supplierItem.width} mm`
                          : null}
                      </div>
                    </div>
                  )}
                  
                  {/* Colour */}
                  {getColourName(supplierItem.colour) && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Colour</div>
                      <div className="text-base text-gray-900">{getColourName(supplierItem.colour)}</div>
                    </div>
                  )}
                  
                  {/* Shape */}
                  {getShapeName(supplierItem.shape) && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Shape</div>
                      <div className="text-base text-gray-900">{getShapeName(supplierItem.shape)}</div>
                    </div>
                  )}
                  
                  {/* Reorder Information */}
                  {(supplierItem.reorderLevel !== undefined || supplierItem.reorderQuantity !== undefined) && (
                    <div>
                      <div className="text-xs text-gray-600 mb-2">Reorder Information</div>
                      {supplierItem.reorderLevel !== undefined && supplierItem.reorderLevel !== null && (
                        <div className="text-sm text-gray-700 mb-1">Reorder Level: {supplierItem.reorderLevel}</div>
                      )}
                      {supplierItem.reorderQuantity !== undefined && supplierItem.reorderQuantity !== null && (
                        <div className="text-sm text-gray-700">Reorder Quantity: {supplierItem.reorderQuantity}</div>
                      )}
                    </div>
                  )}
                  
                  {/* Tax */}
                  {supplierItem.taxPercentage !== undefined && supplierItem.taxPercentage !== null && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Tax Percentage</div>
                      <div className="text-base text-gray-900">{supplierItem.taxPercentage}%</div>
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
        )}

        {/* Package Details Card */}
        {(() => {
          const packageDetails = stockItem?.packageDetails
          
          // Debug: Log package details to verify they're loaded
          if (process.env.NODE_ENV === 'development') {
            console.log('Package Details Debug:', {
              packageDetails: packageDetails,
              allFields: Object.keys(stockItem),
            })
          }
          
          if (!packageDetails) return null
          
          const hasPackageDetails = 
            (packageDetails.length !== undefined && packageDetails.length !== null) ||
            (packageDetails.width !== undefined && packageDetails.width !== null) ||
            (packageDetails.height !== undefined && packageDetails.height !== null) ||
            (packageDetails.weight !== undefined && packageDetails.weight !== null)
          
          if (!hasPackageDetails) return null
          
          // Helper to format dimensions using the stored unit
          const formatDimension = (value: number, unit?: string): string => {
            if (!value && value !== 0) return 'N/A'
            const displayUnit = unit || packageDetails.dimensionUnit || 'mm'
            // Format based on unit - for mm, show as integer; for cm, show 1 decimal
            if (displayUnit === 'mm') {
              return `${Math.round(value)} mm`
            } else if (displayUnit === 'cm') {
              return `${value.toFixed(1)} cm`
            } else if (displayUnit === 'in') {
              return `${value.toFixed(2)} in`
            } else if (displayUnit === 'm') {
              return `${value.toFixed(2)} m`
            }
            return `${value} ${displayUnit}`
          }
          
          // Helper to format weight using the stored unit
          const formatWeight = (value: number, unit?: string): string => {
            if (!value && value !== 0) return 'N/A'
            const displayUnit = unit || packageDetails.weightUnit || 'g'
            // Format based on unit
            if (displayUnit === 'g') {
              return `${Math.round(value)} g`
            } else if (displayUnit === 'kg') {
              return value < 1 ? `${(value * 1000).toFixed(0)} g` : `${value.toFixed(3)} kg`
            } else if (displayUnit === 'lb') {
              return `${value.toFixed(2)} lb`
            } else if (displayUnit === 'oz') {
              return `${value.toFixed(1)} oz`
            }
            return `${value} ${displayUnit}`
          }
          
          const dimensionUnit = packageDetails.dimensionUnit || 'mm'
          const weightUnit = packageDetails.weightUnit || 'g'
          
          return (
            <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Package Details</h3>
              <div className="space-y-4">
                {/* Dimensions Summary */}
                {(() => {
                  const hasDimensions = 
                    (packageDetails.length !== undefined && packageDetails.length !== null) &&
                    (packageDetails.width !== undefined && packageDetails.width !== null) &&
                    (packageDetails.height !== undefined && packageDetails.height !== null)
                  
                  if (!hasDimensions) return null
                  
                  return (
                    <div>
                      <div className="text-xs text-gray-600 mb-2">Dimensions</div>
                      <div className="text-base text-gray-900 font-medium mb-3">
                        {formatDimension(packageDetails.length!, dimensionUnit)} ×{' '}
                        {formatDimension(packageDetails.width!, dimensionUnit)} ×{' '}
                        {formatDimension(packageDetails.height!, dimensionUnit)}
                      </div>
                    </div>
                  )
                })()}

                {/* Individual Dimensions */}
                {(() => {
                  const hasDimensions = 
                    (packageDetails.length !== undefined && packageDetails.length !== null) ||
                    (packageDetails.width !== undefined && packageDetails.width !== null) ||
                    (packageDetails.height !== undefined && packageDetails.height !== null)
                  
                  if (!hasDimensions) return null
                  
                  return (
                    <div>
                      <div className="text-xs text-gray-600 mb-2">Individual Dimensions</div>
                      <div className="grid grid-cols-3 gap-3">
                        {packageDetails.length !== undefined && packageDetails.length !== null && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Length</div>
                            <div className="text-base text-gray-900">{formatDimension(packageDetails.length, dimensionUnit)}</div>
                          </div>
                        )}
                        {packageDetails.width !== undefined && packageDetails.width !== null && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Width</div>
                            <div className="text-base text-gray-900">{formatDimension(packageDetails.width, dimensionUnit)}</div>
                          </div>
                        )}
                        {packageDetails.height !== undefined && packageDetails.height !== null && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Height</div>
                            <div className="text-base text-gray-900">{formatDimension(packageDetails.height, dimensionUnit)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })()}

                {/* Weight */}
                {packageDetails.weight !== undefined && packageDetails.weight !== null && (
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Weight</div>
                    <div className="text-base text-gray-900">{formatWeight(packageDetails.weight, weightUnit)}</div>
                  </div>
                )}
              </div>
            </div>
          )
        })()}

        {/* Attributes Card */}
        {stockItem?.attributes && Object.keys(stockItem.attributes).length > 0 && (
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Attributes</h3>
            {formatAttributes(stockItem.attributes)}
          </div>
        )}

        {/* Used in Bundles Card - Always visible */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Used in Bundles</h3>
          {bundlesLoading ? (
            <div className="text-sm text-gray-500">Loading bundles...</div>
          ) : bundlesError ? (
            <div className="text-sm text-red-600">
              <div>Error loading bundles: {bundlesError instanceof Error ? bundlesError.message : 'Unknown error'}</div>
              {process.env.NODE_ENV === 'development' && (
                <details className="mt-2">
                  <summary className="text-xs cursor-pointer">Error details</summary>
                  <pre className="mt-2 text-xs overflow-auto bg-red-50 p-2 rounded">{JSON.stringify(bundlesError, null, 2)}</pre>
                </details>
              )}
            </div>
          ) : (
            <>
              <div className="text-sm text-gray-600 mb-3">
                {bundles.length === 0
                  ? '0 bundles use this stock item'
                  : `${bundles.length} ${bundles.length === 1 ? 'bundle' : 'bundles'} use this stock item`}
              </div>
              {bundles.length > 0 ? (
                <div className="space-y-2">
                  {bundles.map((bundle) => (
                    <Link
                      key={bundle._id}
                      to={`/bundles/${bundle._id}`}
                      className="block bg-gray-50 rounded-md p-3 hover:bg-gray-100 transition-colors touch-target-min-44"
                    >
                      <div className="font-medium text-gray-900 mb-1">{bundle.title}</div>
                      <div className="text-xs text-gray-600">Qty: {bundle.quantity}</div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic">No bundles currently use this stock item.</div>
              )}
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Link
            to="/stock-items"
            className="flex-1 bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium text-center touch-target-min-44 hover:bg-gray-200 transition-colors"
          >
            Back to List
          </Link>
          <Link
            to={`/stock-items/${id}/edit`}
            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium text-center touch-target-min-44 hover:bg-blue-700 transition-colors"
          >
            Edit Item
          </Link>
        </div>
      </main>
    </div>
  )
}
